import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AssignBlock, AssignBox, AssignHead, BottomSection, CalenderMainDiv, Div, RightSide, Weekly } from './styles';
import Actions from './actions';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { getRawComment, isEmpty } from '../../helpers/common';
import { nanoid } from 'nanoid';
import { COMMENT_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_SIZE } from '../../global/constants';
import { RawUpdateCommentInterface } from '../../interfaces/TaskInterface';
import { trackAnalyticActivity } from '../../services/analyticsService';
import { createNewComment, updateRecurringTaskDetails, updateTaskDetails } from '../../services/taskServices';
import Submenu from './subMenuWeeklyMenu';
import { TASK_ANALYTICS } from '../../global/analyticsConstants';
import { Dropdown, DropdownDivider, DropdownItem } from '../dropdown';
import SubMenuMonthly from './subMenuMonthly';
import CalendarComponentV2 from './calendarComponentV2';
import { TaskModel } from '../../mobx/models/task';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import IconButton from '../button/iconButton';

dayjs.extend(utc);

// Interface for the Dropdown component's ref methods
interface DropdownHandle {
  closeDropdown: () => void;
}

// Interface for the payload used in task updates
interface TaskUpdatePayload {
  dueDate?: string | null;
  projectId?: string;
  dueDateUnset?: boolean;
  start?: string | null;
  recurringInterval?: string | null;
}

// Interface for the temporary payload object used in callbacks
interface TaskActionPayload {
  id?: string;
  previousDueDate?: string;
  dueDate?: string;
  companyId?: string;
  projectId?: string;
}

interface Props {
  taskData?: TaskModel;
  loadData?: () => void;
  isCreateTask?: boolean;
  calendarDate: Date;
  setCalendarDate?: (date: Date) => void;
  setLinkDueDate?: (value: string | undefined) => void;
  isShareDocModel?: boolean;
  isRecurringTask?: boolean;
  closeContextMenu?: () => void;
}

function CalendarDropdownMenuV2(props: Props) {
  const {
    taskData,
    isCreateTask = false,
    calendarDate,
    setCalendarDate,
    setLinkDueDate,
    isRecurringTask = false,
    closeContextMenu
  } = props;

  // States
  const [isSelectedRepeat, setIsSelectedRepeat] = useState<boolean>(false);
  // Typing the ref specifically for the Dropdown component's handle
  const dailyTaskRef = useRef<DropdownHandle>(null);
  //Mobx store variables
  const { currentUser, taskStore, workspaceStore } = rootStore;
  const { createTaskInput } = taskStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const workspace_id = workspace?.id;

  //Other variables
  const day = dayjs().format('ddd');
  const tomorrow = dayjs().add(1, 'day').format('ddd');

  useEffect(() => {
    if (!isEmpty(createTaskInput?.recurringInterval)) {
      setIsSelectedRepeat(true);
    } else {
      setIsSelectedRepeat(false);
    }
  }, [createTaskInput?.recurringInterval]);

  const getDueDateAndDisplay = useCallback((input: string): { dueDate: string | undefined; display: string } => {
    switch (input) {
      case 'today':
        return { dueDate: dayjs().toISOString(), display: 'Today' };
      case 'tomorrow':
        return { dueDate: dayjs().add(1, 'day').toISOString(), display: 'Tomorrow' };
      case 'nextweek':
        return { dueDate: dayjs().day(8).toISOString(), display: 'Next Week' };
      case 'nodate':
        return { dueDate: undefined, display: '' };
      default:
        return { dueDate: undefined, display: '' };
    }
  }, []);

  const handleTaskDetailsForNoDate = useCallback(
    async (payload: TaskActionPayload, payloadTask: TaskUpdatePayload) => {
      if (!currentUser || !payload.id) return;
      const newComment = {
        id: nanoid(),
        type: COMMENT_TYPE.REMOVE_DUE_DATE
      };
      const updatedComment = getRawComment(newComment, currentUser);
      taskData?.setDueDate(null);
      taskData?.upsertComment(updatedComment);

      const response = await updateTaskDetails(payload?.id, payloadTask);
      const result = await createNewComment(payload?.id, {
        Type: COMMENT_TYPE.REMOVE_DUE_DATE
      });
      if (response && result) props?.loadData?.();
    },
    [currentUser, props, taskData]
  );

  const handleNoDate = useCallback(
    async (payload: TaskActionPayload) => {
      const payloadTask = { dueDateUnset: true, projectId: payload?.projectId };

      if (isRecurringTask) {
        const response = await updateRecurringTaskDetails(payload?.id, payloadTask);
        if (response) props?.loadData?.();
      } else {
        await handleTaskDetailsForNoDate(payload, payloadTask);
      }
    },
    [handleTaskDetailsForNoDate, isRecurringTask, props]
  );

  const trackDueDateActivity = useCallback((type: number) => {
    if (type === COMMENT_TYPE.SET_DUE_DATE) trackAnalyticActivity(TASK_ANALYTICS.SCHEDULED);
    else trackAnalyticActivity(TASK_ANALYTICS.RESCHEDULED);
  }, []);

  const updateTaskDetailsAndComments = useCallback(
    async (payload: TaskActionPayload, newComment: RawUpdateCommentInterface, payloadTask: TaskUpdatePayload) => {
      if (!currentUser) return;

      taskData?.setDueDate(payload?.dueDate ? new Date(payload?.dueDate) : null);
      const updatedComment = getRawComment(newComment, currentUser);
      taskData?.upsertComment(updatedComment);
      const commentPayload = {
        Type: newComment.type,
        DueDate: payload?.dueDate,
        PreviousDueDate: newComment.previousDueDate
      };
      const response = await updateTaskDetails(payload?.id, payloadTask);
      const result = await createNewComment(payload?.id, commentPayload);
      if (response && result) {
        trackDueDateActivity(newComment.type);
      }
    },
    [currentUser, taskData, trackDueDateActivity]
  );

  const handleDueDateUpdate = useCallback(
    async (dueDate: string | undefined, payload: TaskActionPayload) => {
      const newComment: RawUpdateCommentInterface = {
        id: nanoid(),
        type: COMMENT_TYPE.SET_DUE_DATE,
        dueDate: dueDate
      };

      if (payload?.previousDueDate) {
        newComment.type = COMMENT_TYPE.CHANGE_DUE_DATE;
        newComment.previousDueDate = taskData?.dueDate?.toISOString() || '';
      }
      const payloadTask = { dueDate, projectId: taskData?.projectId };
      if (isRecurringTask) {
        const response = await updateRecurringTaskDetails(payload?.id, payloadTask);
        if (response && newComment.type) {
          trackDueDateActivity(newComment.type);
        }
      } else {
        await updateTaskDetailsAndComments(payload, newComment, payloadTask);
      }
    },
    [isRecurringTask, taskData, trackDueDateActivity, updateTaskDetailsAndComments]
  );

  const onClickTime = useCallback(
    async (input: string) => {
      const { dueDate } = getDueDateAndDisplay(input);
      if (
        dueDate &&
        taskData?.dueDate &&
        new Date(dueDate).toDateString() === new Date(taskData.dueDate).toDateString()
      ) {
        return;
      }
      if (!props?.isShareDocModel) {
        if (isCreateTask) {
          if (input === 'nodate') {
            rootStore.taskStore.createTaskInput.setDueDate(null);
          } else {
            rootStore.taskStore.createTaskInput.setDueDate(new Date(dueDate!));
          }
        } else {
          const payload = {
            ...taskData,
            id: taskData?.id,
            projectId: taskData?.projectId,
            companyId: workspace_id,
            previousDueDate: taskData?.dueDate?.toISOString() || '',
            dueDate
          };

          if (input === 'nodate') {
            await handleNoDate(payload);
          } else {
            await handleDueDateUpdate(dueDate, payload);
          }
        }
      } else {
        props?.loadData?.();
        if (setLinkDueDate) setLinkDueDate(dueDate);
      }
    },
    [
      getDueDateAndDisplay,
      props,
      isCreateTask,
      taskData,
      workspace_id,
      handleNoDate,
      handleDueDateUpdate,
      setLinkDueDate
    ]
  );

  const setDailyTask = useCallback(async () => {
    try {
      const payloadTask = {
        projectId: taskData?.projectId,
        start: null,
        recurringInterval: 'daily'
      };
      const response = await updateRecurringTaskDetails(taskData?.id, payloadTask);
      if (response) {
        props?.loadData?.();
      }
      rootStore.taskStore.createTaskInput.setRecurringInterval('daily');
      rootStore.taskStore.createTaskInput.setStart(null);

      if (dailyTaskRef.current) {
        dailyTaskRef.current.closeDropdown();
      }
    } catch (error) {
      console.log('error', error);
    }
  }, [props, taskData?.id, taskData?.projectId]);

  const onRemoveRecurring = useCallback(() => {
    try {
      rootStore.taskStore.createTaskInput.setStart(null);
      rootStore.taskStore.createTaskInput.setRecurringInterval(null);
    } catch (error) {
      console.log('error', error);
    }
  }, []);

  const handleDropdownClick = useCallback(
    (input: string, e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      onClickTime(input);
      closeContextMenu?.();
    },
    [onClickTime, closeContextMenu]
  );

  const recurringMenuDropdown = useMemo(() => {
    return (
      <>
        <Dropdown
          modalTitle='Repeat'
          ref={dailyTaskRef}
          content={
            <Div isSelectedRepeat={isSelectedRepeat || isRecurringTask}>
              <RightSide>
                <SVGIcon
                  name='repeat-icon'
                  width={ICON_SIZE.MD}
                  height={ICON_SIZE.MD}
                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                  fill='var(--text-primary)'
                />
                <p>Repeat</p>
              </RightSide>
              {(isSelectedRepeat || isRecurringTask) && (
                <Weekly>
                  <p>
                    {isRecurringTask && !isSelectedRepeat
                      ? taskData?.recurringInterval
                      : createTaskInput?.recurringInterval}
                  </p>
                  {!isRecurringTask && (
                    <IconButton
                      iconName='new-close-icon'
                      size={ICON_BUTTON_SIZE.EXTRA_SMALL}
                      variant={ICON_BUTTON_TYPE.TERTIARY}
                      onClick={onRemoveRecurring}
                    />
                  )}
                </Weekly>
              )}
            </Div>
          }
          trigger='click'>
          <DropdownItem
            label='Daily'
            onClick={(e?: React.SyntheticEvent) => {
              e?.stopPropagation();
              e?.preventDefault();
              setDailyTask();
            }}
          />
          <Dropdown content={<DropdownItem label='Weekly' />} trigger='hover' isRemoveMaxHeight>
            <Submenu data={taskData} loadData={props?.loadData} />
          </Dropdown>
          <Dropdown content={<DropdownItem label='Monthly' />} trigger='hover'>
            <SubMenuMonthly data={taskData} loadData={props?.loadData} />
          </Dropdown>
        </Dropdown>
      </>
    );
  }, [
    isSelectedRepeat,
    isRecurringTask,
    props?.loadData,
    createTaskInput?.recurringInterval,
    onRemoveRecurring,
    setDailyTask,
    taskData
  ]);

  return (
    <AssignBox>
      <AssignHead>
        <Actions date={calendarDate} />
      </AssignHead>
      <DropdownDivider />
      <AssignBlock>
        <DropdownItem
          label='Today'
          iconName='today-icon'
          iconSize={16}
          iconViewBox='0 0 24 24'
          onClick={(e?: React.SyntheticEvent) => handleDropdownClick('today', e)}
          isSuffix={day}
        />
        <DropdownItem
          label='Tomorrow'
          iconName='tomorrow-icon'
          iconSize={16}
          iconViewBox='0 0 24 24'
          onClick={(e?: React.SyntheticEvent) => handleDropdownClick('tomorrow', e)}
          isSuffix={tomorrow}
        />
        <DropdownItem
          label='Next Week'
          iconName='next-week-icon'
          iconSize={16}
          iconViewBox='0 0 24 24'
          onClick={(e?: React.SyntheticEvent) => handleDropdownClick('nextweek', e)}
          isSuffix='Mon'
        />
        <DropdownItem
          label='No Date'
          iconName='no-date-icon'
          iconSize={16}
          iconViewBox='0 0 24 24'
          onClick={(e?: React.SyntheticEvent) => handleDropdownClick('nodate', e)}
        />
        <DropdownDivider />
        <CalenderMainDiv className='calender'>
          <CalendarComponentV2
            date={calendarDate}
            setCalendarDate={setCalendarDate}
            taskData={taskData}
            isUpdateReducer={isCreateTask}
            loadData={props?.loadData}
            isShareDocModel={props?.isShareDocModel}
            setLinkDate={setLinkDueDate}
            isRecurringTask={isRecurringTask}
            closeDropdowns={closeContextMenu}
          />
        </CalenderMainDiv>
      </AssignBlock>
      {(isCreateTask || isRecurringTask) && (
        <>
          <DropdownDivider />
          <BottomSection isSelectedRepeat={isSelectedRepeat || isRecurringTask}>{recurringMenuDropdown}</BottomSection>
        </>
      )}
    </AssignBox>
  );
}

export default observer(CalendarDropdownMenuV2);
